/**
 * Screen Door Quote Funnel (V4) — dedicated `/screen-door-quote` route.
 *
 * Mobile-first configurator that reuses the existing intake pipeline
 * (`POST /api/intake/submit`) with `funnel_version: "v4_doors"` so
 * lead capture, estimate SMS, booking, and reports keep working while
 * the reporting side can still slice V4 separately.
 *
 * Flow:
 *   1. New Door vs Rescreen (visual)
 *   2a. New Door: quantity → color → mesh → heavy-duty upgrade
 *   2b. Rescreen: quantity → mesh
 *   3. Window screen cross-sell
 *   4. Contact + address gate
 *   5. Instant quote reveal with itemized summary
 */
import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { getAttribution } from "@/utils/attribution";
import { MeshPhoto, meshKindFromName } from "@/components/MeshPhoto";
import { toast } from "sonner";
import {
  DoorOpen, Wrench, ChevronLeft, ChevronRight, Check, Sparkles,
  Sun, Bug, Shield, Truck, Ruler, Loader2, ShieldCheck, Home,
} from "lucide-react";

const BACKEND_URL =
  window.APP_CONFIG?.BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  window.location.origin;
const API = `${BACKEND_URL}/api`;

// Static config — mirrors pricing catalog seed values. Backend is source
// of truth; these are used for the live in-page preview only.
const DOOR_BASE = { new: 379, rescreen: 110 };
const MESHES = [
  { id: "standard", label: "Standard", icon: DoorOpen, price: 0, hint: "Reliable and affordable. Great for most homes." },
  { id: "petscreen", label: "Pet Screen", icon: Shield, price: 30, hint: "Tear-resistant. Best if you have pets.", popular: true },
  { id: "solar",    label: "Solar Sun-Blocking", icon: Sun, price: 50, hint: "Blocks up to 80% of sun heat and glare. Best for hot-facing doors." },
  { id: "no_see_um", label: "No-See-Um", icon: Bug, price: 40, hint: "Fine weave — stops even the tiniest bugs." },
];
const COLORS = [
  { id: "white",  label: "White", hex: "#f8f8f8", ring: "#e5e5e5" },
  { id: "almond", label: "Almond", hex: "#e6ddc4", ring: "#d4c9a3" },
  { id: "black",  label: "Black", hex: "#1a1a1a", ring: "#0a0a0a" },
];
const HEAVY_DUTY_PRICE = 50;

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const TAX_RATE = 0.0875;

function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
    utm_content: p.get("utm_content"),
    utm_term: p.get("utm_term"),
    gclid: p.get("gclid"),
    fbclid: p.get("fbclid"),
  };
}

export default function ScreenDoorQuote() {
  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);           // 'new' | 'rescreen'
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState("white");
  const [mesh, setMesh] = useState("standard");
  const [heavyDuty, setHeavyDuty] = useState(false);
  const [windowChoice, setWindowChoice] = useState(null); // 'rescreen'|'new'|'both'|'no'
  const [windowQty, setWindowQty] = useState(0);           // rescreen count (or "no" total)
  const [windowNewQty, setWindowNewQty] = useState(0);     // new-build count (used only when choice='both')

  // Contact
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [quoteReady, setQuoteReady] = useState(false);
  const [leadId, setLeadId] = useState(null);

  // Auto-advance the moment the service branch is picked (step 0). Using
  // useEffect avoids the stale-closure trap that came from calling onNext
  // inside a setTimeout right after setService.
  useEffect(() => {
    if (step === 0 && service) {
      const t = setTimeout(() => setStep(1), 200);
      return () => clearTimeout(t);
    }
  }, [service, step]);

  // Track visit once on mount (fire-and-forget; ignore errors — funnel must always render)
  const [sessionId] = useState(() => `sd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  useEffect(() => {
    const utm = getUTM();
    axios.post(`${API}/funnel/track`, {
      session_id: sessionId,
      step: 0,
      step_name: "visit",
      event_type: "visit",
      funnel_version: "v4_doors",
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      metadata: {
        referrer: document.referrer || null,
        landing_page: window.location.pathname,
      },
    }).catch(() => {});
  }, []);

  // Steps depend on the branch — new-door has 4 config steps, rescreen has 2.
  const configSteps = service === "rescreen"
    ? ["qty", "mesh", "windows", "contact"]
    : service ? ["qty", "color", "mesh", "heavyduty", "windows", "contact"] : [];
  const totalSteps = 1 + configSteps.length; // +1 for the initial branch
  const stepId = step === 0 ? "service" : configSteps[step - 1];

  const meshCfg = MESHES.find(m => m.id === mesh) || MESHES[0];

  // ---------- pricing ----------
  const priceBreakdown = useMemo(() => {
    const items = [];
    if (!service) return { items, subtotal: 0, tax: 0, total: 0 };

    const doorBase = DOOR_BASE[service];
    const colorLabel = COLORS.find((c) => c.id === color)?.label;
    const doorName = service === "new" ? "New Sliding Screen Door" : "Sliding Door Rescreen";
    items.push({
      name: colorLabel ? `${doorName} (${colorLabel})` : doorName,
      quantity: qty, unit_price: doorBase, line_total: doorBase * qty,
      taxable: service === "new",
    });
    if (meshCfg.price > 0) {
      items.push({
        name: meshCfg.label + " Mesh Upgrade", quantity: qty, unit_price: meshCfg.price,
        line_total: meshCfg.price * qty, taxable: false,
      });
    }
    if (service === "new" && heavyDuty) {
      items.push({
        name: "Heavy Duty Upgrade", quantity: qty, unit_price: HEAVY_DUTY_PRICE,
        line_total: HEAVY_DUTY_PRICE * qty, taxable: true,
      });
    }
    if (windowChoice && windowChoice !== "no") {
      // For "rescreen" and "new" the single `windowQty` field applies.
      // For "both" the admin (well — customer) enters counts independently.
      const rescreenQty = windowChoice === "both" ? windowQty : (windowChoice === "rescreen" ? windowQty : 0);
      const newQty      = windowChoice === "both" ? windowNewQty : (windowChoice === "new" ? windowQty : 0);
      if (rescreenQty > 0) {
        items.push({ name: "Window Screen Rescreen", quantity: rescreenQty, unit_price: 50, line_total: 50 * rescreenQty, taxable: false });
      }
      if (newQty > 0) {
        items.push({ name: "New Window Screen", quantity: newQty, unit_price: 70, line_total: 70 * newQty, taxable: true });
      }
    }
    const subtotal = items.reduce((s, i) => s + i.line_total, 0);
    const tax = items.filter(i => i.taxable).reduce((s, i) => s + i.line_total, 0) * TAX_RATE;
    return { items, subtotal, tax, total: subtotal + tax };
  }, [service, qty, color, mesh, heavyDuty, windowChoice, windowQty, windowNewQty, meshCfg]);

  const goNext = () => {
    // Simple validation per step
    if (stepId === "service" && !service) return toast.error("Pick one to continue");
    if (stepId === "qty" && qty < 1) return toast.error("How many doors?");
    if (stepId === "windows") {
      if (!windowChoice) return toast.error("Pick an option");
      if (windowChoice === "rescreen" && !(windowQty > 0)) return toast.error("How many to rescreen?");
      if (windowChoice === "new" && !(windowQty > 0)) return toast.error("How many new screens?");
      if (windowChoice === "both" && !(windowQty > 0 || windowNewQty > 0)) return toast.error("Enter at least one count");
    }
    if (stepId === "contact") return handleSubmit();
    setStep(step + 1);
  };
  const goBack = () => setStep(Math.max(0, step - 1));

  const submissionIdRef = useRef(null);

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error("First name is required");
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10) return toast.error("Enter a valid 10-digit phone number");
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRe.test(email.trim())) return toast.error("Enter a valid email address");
    if (!address.trim()) return toast.error("Address is required so we can confirm we service your area");

    // If we already have a lead_id from a previous attempt (e.g. the customer
    // tapped again after a transient error but the backend actually saved
    // the lead), skip the POST entirely and just show them the quote.
    // Otherwise generate a stable idempotency key for this submission so
    // rage-tapping collapses into a single lead server-side.
    if (leadId) {
      setQuoteReady(true);
      return;
    }
    if (!submissionIdRef.current) {
      submissionIdRef.current = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `sub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }

    setSubmitting(true);
    try {
      const payload = {
        service_type: service === "new" ? "new_door" : "rescreen_door",
        screen_count: qty + (windowQty || 0),
        screen_type: `door_${mesh}${heavyDuty ? "_hd" : ""}_${color}`,
        full_name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim() || "Not provided yet",
        zip_code: zip.trim(),
        funnel_version: "v4_doors",
        source: "screenzz.com",
        ...getAttribution(),
        submission_id: submissionIdRef.current,
        ...getUTM(),
        referrer: document.referrer || null,
        landing_page: "/screen-door-quote",
        estimate_data: {
          service, qty, color, mesh, heavy_duty: heavyDuty,
          window_choice: windowChoice, window_qty: windowQty, window_new_qty: windowNewQty,
          line_items: priceBreakdown.items,
          subtotal: priceBreakdown.subtotal,
          tax: priceBreakdown.tax,
          total: priceBreakdown.total,
        },
      };
      const { data } = await axios.post(`${API}/intake/submit`, payload);
      setLeadId(data.lead_id);
      setQuoteReady(true);
      // Log the funnel completion event (best-effort).
      axios.post(`${API}/funnel/track`, {
        session_id: sessionId, step: 99, step_name: "quote_revealed",
        event_type: "quote_revealed", funnel_version: "v4_doors",
        metadata: { lead_id: data.lead_id, quote_value: priceBreakdown.total },
      }).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ============================ RENDER ============================
  if (quoteReady) return <QuoteReveal breakdown={priceBreakdown} service={service} color={color} mesh={meshCfg} heavyDuty={heavyDuty} windowChoice={windowChoice} windowQty={windowQty} windowNewQty={windowNewQty} name={name} leadId={leadId} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-32">
      <header className="max-w-2xl mx-auto px-4 pt-5 pb-3">
        <div className="flex flex-col items-center gap-1.5">
          <img
            src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
            alt="ScreenzZ"
            className="h-10 sm:h-12 w-auto"
            data-testid="funnel-logo"
          />
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#207024] font-semibold">Screen Door Quote</div>
        </div>
      </header>

      {/* Progress bar */}
      {step > 0 && (
        <div className="max-w-2xl mx-auto px-4 mb-4">
          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#207024] transition-all duration-300"
              style={{ width: `${(step / (totalSteps - 1)) * 100}%` }}
            />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1.5 flex justify-between">
            <button onClick={goBack} className="text-[#207024] font-semibold flex items-center gap-1" data-testid="back-btn">
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <span>Step {step} of {totalSteps - 1}</span>
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4">
        {stepId === "service" && <StepService service={service} setService={setService} />}
        {stepId === "qty" && <StepQty qty={qty} setQty={setQty} service={service} />}
        {stepId === "color" && <StepColor color={color} setColor={setColor} />}
        {stepId === "mesh" && <StepMesh mesh={mesh} setMesh={setMesh} />}
        {stepId === "heavyduty" && <StepHeavyDuty heavyDuty={heavyDuty} setHeavyDuty={setHeavyDuty} />}
        {stepId === "windows" && (
          <StepWindows
            choice={windowChoice} setChoice={setWindowChoice}
            qty={windowQty} setQty={setWindowQty}
            newQty={windowNewQty} setNewQty={setWindowNewQty}
          />
        )}
        {stepId === "contact" && (
          <StepContact
            name={name} setName={setName} phone={phone} setPhone={setPhone}
            email={email} setEmail={setEmail} address={address} setAddress={setAddress}
            zip={zip} setZip={setZip}
          />
        )}
      </main>

      {/* Sticky footer with order summary + Continue */}
      {step > 0 && stepId !== "service" && (
        <footer className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-zinc-200 shadow-lg">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0" data-testid="order-summary">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Your order</div>
              <ul className="mt-0.5 space-y-0.5">
                {priceBreakdown.items.length === 0 ? (
                  <li className="text-sm text-zinc-500 italic">Building your quote…</li>
                ) : (
                  priceBreakdown.items.map((li, i) => (
                    <li key={i} className="text-sm text-zinc-800 truncate">
                      <span className="font-semibold text-[#207024]">{li.quantity}×</span> {li.name}
                    </li>
                  ))
                )}
              </ul>
            </div>
            <button
              onClick={goNext}
              disabled={submitting}
              data-testid="continue-btn"
              className="h-12 px-5 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold flex items-center gap-2 disabled:opacity-60 flex-shrink-0"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>)
                : stepId === "contact" ? (<>See My Quote <Sparkles className="w-4 h-4" /></>)
                : (<>Continue <ChevronRight className="w-4 h-4" /></>)}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

// ============================ STEP COMPONENTS ============================

function StepService({ service, setService }) {
  return (
    <div className="space-y-5">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-zinc-900">Let&apos;s Get Your Screen Door Taken Care Of</h1>
        <p className="text-zinc-600 mt-2">Choose an option to get your instant price.</p>
      </div>
      <div className="grid gap-3">
        <ServiceCard
          selected={service === "new"} onClick={() => setService("new")}
          title="New Screen Door" body="Need the entire door replaced? We'll build a brand-new sliding screen door to fit your opening."
          icon={DoorOpen} testId="service-new"
        />
        <ServiceCard
          selected={service === "rescreen"} onClick={() => setService("rescreen")}
          title="Rescreen Existing Door" body="Frame still in good shape? We'll replace the old mesh and make your existing door look great again."
          icon={Wrench} testId="service-rescreen"
        />
      </div>
      <IncludedFreeCard />
    </div>
  );
}

function ServiceCard({ selected, onClick, title, body, icon: Icon, testId }) {
  return (
    <button
      onClick={onClick} data-testid={testId}
      className={`text-left w-full p-5 rounded-2xl border-2 transition-all ${
        selected ? "border-[#207024] bg-green-50 shadow-md" : "border-zinc-200 bg-white hover:border-[#207024]/40"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`h-14 w-14 rounded-xl flex items-center justify-center flex-shrink-0 ${selected ? "bg-[#207024] text-white" : "bg-zinc-100 text-zinc-600"}`}>
          <Icon className="w-7 h-7" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-zinc-900">{title}</h3>
            {selected && <Check className="w-5 h-5 text-[#207024]" />}
          </div>
          <p className="text-sm text-zinc-600 mt-1">{body}</p>
        </div>
      </div>
    </button>
  );
}

function IncludedFreeCard() {
  return (
    <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-emerald-700" />
        <div className="text-xs uppercase tracking-wider text-emerald-800 font-bold">Included FREE with every new door</div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <FreePerk icon={Ruler} label="Professional Measurement" />
        <FreePerk icon={Truck} label="Delivery" />
        <FreePerk icon={ShieldCheck} label="Professional Installation" />
      </div>
      <p className="text-sm text-emerald-800 mt-3 text-center font-medium">You pick your options. We handle the rest.</p>
    </div>
  );
}

function FreePerk({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center border border-emerald-200">
        <Icon className="w-5 h-5 text-emerald-700" />
      </div>
      <div className="text-[11px] font-semibold text-emerald-900 leading-tight">{label}</div>
    </div>
  );
}

function StepQty({ qty, setQty, service }) {
  const label = service === "new" ? "How many new screen doors?" : "How many doors to rescreen?";
  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-zinc-900">{label}</h2>
      <div className="grid grid-cols-4 gap-3">
        {[1,2,3,4].map(n => (
          <button
            key={n} onClick={() => setQty(n)} data-testid={`qty-${n}`}
            className={`h-20 rounded-2xl border-2 text-2xl font-bold transition-all ${
              qty === n ? "border-[#207024] bg-green-50 text-[#207024]" : "border-zinc-200 bg-white text-zinc-700"
            }`}
          >
            {n}{n === 4 ? "+" : ""}
          </button>
        ))}
      </div>
      {qty >= 4 && (
        <div>
          <label className="block text-sm font-semibold text-zinc-700 mb-1">Exact quantity</label>
          <input type="number" min={4} max={20} value={qty} onChange={(e) => setQty(parseInt(e.target.value) || 4)}
            data-testid="qty-input"
            className="w-full h-12 rounded-lg border-2 border-zinc-200 px-4 focus:border-[#207024]" />
        </div>
      )}
    </div>
  );
}

function StepColor({ color, setColor }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Choose your frame color</h2>
        <p className="text-zinc-600 text-sm mt-1">All colors same price.</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {COLORS.map(c => (
          <button
            key={c.id} onClick={() => setColor(c.id)} data-testid={`color-${c.id}`}
            className={`rounded-2xl border-2 p-4 flex flex-col items-center gap-2 transition-all ${
              color === c.id ? "border-[#207024] shadow-md" : "border-zinc-200"
            }`}
          >
            <div className="h-16 w-16 rounded-full border-2" style={{ backgroundColor: c.hex, borderColor: c.ring }} />
            <div className="text-sm font-semibold text-zinc-900">{c.label}</div>
            {color === c.id && <Check className="w-4 h-4 text-[#207024]" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepMesh({ mesh, setMesh }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Which mesh is best for you?</h2>
        <p className="text-zinc-600 text-sm mt-1">Standard is included — upgrade only if it fits your home.</p>
      </div>
      <div className="grid gap-3">
        {MESHES.map(m => {
          const Icon = m.icon;
          const selected = mesh === m.id;
          return (
            <div key={m.id}>
            <button
              onClick={() => setMesh(m.id)} data-testid={`mesh-${m.id}`}
              className={`w-full text-left p-4 rounded-2xl border-2 flex items-start gap-3 transition-all ${
                selected ? "border-[#207024] bg-green-50" : "border-zinc-200 bg-white"
              }`}
            >
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${selected ? "bg-[#207024] text-white" : "bg-zinc-100 text-zinc-600"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-zinc-900">{m.label}</h3>
                    {m.popular && (
                      <span
                        data-testid={`mesh-popular-${m.id}`}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide"
                      >
                        <Sparkles className="w-2.5 h-2.5" /> Most Popular
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-[#207024] shrink-0">{m.price === 0 ? "Included" : `+${fmt(m.price)}/door`}</span>
                </div>
                <p className="text-sm text-zinc-600 mt-0.5">{m.hint}</p>
              </div>
            </button>
            {(m.id === "petscreen" || m.id === "solar") && <div className="px-2"><MeshPhoto kind={m.id} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const HD_SLIDES = [
  { src: "/hd-upgrade/hd-3.jpg", caption: "Beefier frame & bigger handle — 2½″ vs 1¾″ frame, 5″ vs 3¼″ handle" },
  { src: "/hd-upgrade/hd-2.jpg", caption: "Side-by-side: the Heavy Duty frame profile is noticeably thicker & sturdier" },
  { src: "/hd-upgrade/hd-1.jpg", caption: "Stainless-steel 1¼″ sealed-bearing rollers glide smoother and last far longer" },
];

function HeavyDutySlideshow() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % HD_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, [paused]);

  const go = (delta) => {
    setPaused(true);
    setIdx((i) => (i + delta + HD_SLIDES.length) % HD_SLIDES.length);
  };
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <div
      className="relative rounded-2xl overflow-hidden bg-zinc-100 border border-zinc-200"
      data-testid="hd-slideshow"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative aspect-[4/5] sm:aspect-[4/3] w-full bg-zinc-100">
        {HD_SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.caption}
            loading={i === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        <button
          type="button"
          aria-label="Previous image"
          data-testid="hd-slide-prev"
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-zinc-800"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Next image"
          data-testid="hd-slide-next"
          onClick={(e) => { e.stopPropagation(); go(1); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-zinc-800"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      <div className="px-4 py-3 bg-white border-t border-zinc-200">
        <p className="text-xs sm:text-sm text-zinc-700 text-center min-h-[2.25rem]">{HD_SLIDES[idx].caption}</p>
        <div className="flex justify-center gap-1.5 mt-2">
          {HD_SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hd-slide-dot-${i}`}
              onClick={(e) => { e.stopPropagation(); setPaused(true); setIdx(i); }}
              className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-[#207024]" : "w-2 bg-zinc-300"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function StepHeavyDuty({ heavyDuty, setHeavyDuty }) {
  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-zinc-900">Add the Heavy Duty upgrade?</h2>
      <p className="text-sm text-zinc-600 -mt-2">See the difference — standard vs heavy duty:</p>
      <HeavyDutySlideshow />
      <div className="grid gap-3">
        <button
          onClick={() => setHeavyDuty(true)} data-testid="hd-yes"
          className={`text-left p-5 rounded-2xl border-2 ${heavyDuty ? "border-[#207024] bg-green-50" : "border-zinc-200 bg-white"}`}
        >
          <div className="flex items-start gap-3">
            <Shield className="w-8 h-8 text-[#207024] flex-shrink-0" />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-zinc-900">Yes — upgrade to Heavy Duty</h3>
                <span className="text-sm font-semibold text-[#207024]">+{fmt(HEAVY_DUTY_PRICE)}/door</span>
              </div>
              <p className="text-sm text-zinc-600 mt-1">Reinforced, thicker frame with larger inside &amp; outside handles. Best for high-traffic doors, pets, and homes with kids.</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => setHeavyDuty(false)} data-testid="hd-no"
          className={`text-left p-5 rounded-2xl border-2 ${!heavyDuty ? "border-[#207024] bg-green-50" : "border-zinc-200 bg-white"}`}
        >
          <h3 className="font-bold text-zinc-900">No thanks — standard is fine</h3>
        </button>
      </div>
    </div>
  );
}

function StepWindows({ choice, setChoice, qty, setQty, newQty, setNewQty }) {
  const options = [
    { id: "rescreen", label: "Rescreen my window screens", body: "Frames are fine, mesh needs replacing." },
    { id: "new",      label: "New window screens",         body: "Missing or broken — need new ones built." },
    { id: "both",     label: "Both — rescreen some, new some", body: "" },
    { id: "no",       label: "Just the door, thanks",       body: "" },
  ];
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">While we&apos;re there…</h2>
        <p className="text-zinc-600 text-sm mt-1">Any window screens need attention? We&apos;re already coming out, so we can knock it out same trip.</p>
      </div>
      <div className="grid gap-3">
        {options.map(o => (
          <button
            key={o.id} onClick={() => setChoice(o.id)} data-testid={`win-${o.id}`}
            className={`text-left p-4 rounded-2xl border-2 ${choice === o.id ? "border-[#207024] bg-green-50" : "border-zinc-200 bg-white"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Home className="w-5 h-5 text-zinc-600" />
                <h3 className="font-bold text-zinc-900">{o.label}</h3>
              </div>
              {choice === o.id && <Check className="w-5 h-5 text-[#207024]" />}
            </div>
            {o.body && <p className="text-sm text-zinc-600 mt-1 ml-7">{o.body}</p>}
          </button>
        ))}
      </div>

      {choice === "rescreen" && (
        <QtyField label="How many screens need rescreening?" value={qty} onChange={setQty} testId="window-qty-input" />
      )}
      {choice === "new" && (
        <QtyField label="How many new screens do you need?" value={qty} onChange={setQty} testId="window-qty-input" />
      )}
      {choice === "both" && (
        <div className="grid gap-3 rounded-2xl border-2 border-zinc-200 bg-zinc-50 p-3">
          <QtyField label="Rescreen (mesh only)" value={qty} onChange={setQty} testId="window-rescreen-qty-input" />
          <QtyField label="New builds" value={newQty} onChange={setNewQty} testId="window-new-qty-input" />
          <p className="text-[11px] text-zinc-500">Enter how many of each — this keeps your quote accurate.</p>
        </div>
      )}
    </div>
  );
}

function QtyField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-zinc-700 mb-1">{label}</label>
      <input type="number" min={1} max={40} value={value || ""} onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        data-testid={testId} placeholder="e.g. 6"
        className="w-full h-12 rounded-lg border-2 border-zinc-200 bg-white px-4 focus:border-[#207024]" />
    </div>
  );
}

function StepContact({ name, setName, phone, setPhone, email, setEmail, address, setAddress, zip, setZip }) {
  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Google Places Autocomplete — mirrors the pattern in Intake.js / CustomerDialog.js
  useEffect(() => {
    const init = () => {
      if (!addressRef.current || autocompleteRef.current) return;
      if (!(window.google && window.google.maps && window.google.maps.places)) return;
      autocompleteRef.current = new window.google.maps.places.Autocomplete(addressRef.current, {
        componentRestrictions: { country: "us" },
        fields: ["address_components", "formatted_address"],
        types: ["address"],
      });
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.address_components) return;
        let zipCode = "";
        for (const c of place.address_components) {
          if (c.types.includes("postal_code")) zipCode = c.long_name;
        }
        setAddress(place.formatted_address || addressRef.current.value);
        if (zipCode) setZip(zipCode);
      });
    };
    if (window.googleMapsReady) init();
    else {
      const prev = window.onGoogleMapsReady;
      window.onGoogleMapsReady = () => { if (prev) prev(); init(); };
    }
    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      autocompleteRef.current = null;
    };
  }, [setAddress, setZip]);

  const formatPhone = (raw) => {
    const d = raw.replace(/\D/g, "").slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Almost done — where should we send it?</h2>
        <p className="text-zinc-600 text-sm mt-1">Your quote is ready. Just need your info to text it over.</p>
      </div>
      <div className="grid gap-3">
        <Field label="First name" value={name} onChange={setName} testId="contact-name" autoComplete="given-name" />
        <Field
          label="Phone"
          value={phone}
          onChange={(v) => setPhone(formatPhone(v))}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="(555) 123-4567"
          testId="contact-phone"
          maxLength={14}
        />
        <Field label="Email" value={email} onChange={setEmail} type="email" autoComplete="email" testId="contact-email" placeholder="you@example.com" />
        <label className="block">
          <span className="block text-sm font-semibold text-zinc-700 mb-1">Street address</span>
          <input
            ref={addressRef}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            type="text"
            autoComplete="street-address"
            placeholder="Start typing your address…"
            data-testid="contact-address"
            className="w-full h-12 rounded-lg border-2 border-zinc-200 px-4 focus:border-[#207024] focus:outline-none"
          />
          <span className="block text-[11px] text-zinc-500 mt-1">So we can confirm we service your area.</span>
        </label>
      </div>
      <p className="text-[11px] text-zinc-500 text-center">By continuing you agree to receive a text with your quote. Reply STOP to opt out.</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, inputMode, maxLength, placeholder, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-zinc-700 mb-1">{label}</span>
      <input
        value={value} onChange={(e) => onChange(e.target.value)}
        type={type} inputMode={inputMode} maxLength={maxLength} placeholder={placeholder} autoComplete={autoComplete}
        data-testid={testId}
        className="w-full h-12 rounded-lg border-2 border-zinc-200 px-4 focus:border-[#207024] focus:outline-none"
      />
    </label>
  );
}

function QuoteReveal({ breakdown, service, color, mesh, heavyDuty, windowChoice, windowQty, windowNewQty, name, leadId }) {
  const winSummary = (() => {
    if (!windowChoice || windowChoice === "no") return null;
    if (windowChoice === "rescreen" && windowQty > 0) return `${windowQty} window screen${windowQty === 1 ? "" : "s"} — rescreen`;
    if (windowChoice === "new" && windowQty > 0)      return `${windowQty} window screen${windowQty === 1 ? "" : "s"} — new build`;
    if (windowChoice === "both") {
      const parts = [];
      if (windowQty > 0) parts.push(`${windowQty} rescreen`);
      if (windowNewQty > 0) parts.push(`${windowNewQty} new`);
      return parts.length ? `Window screens — ${parts.join(" + ")}` : null;
    }
    return null;
  })();
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-16">
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-3">
            <Check className="w-4 h-4" /> Quote Ready
          </div>
          <h1 className="text-3xl font-bold text-zinc-900">Here&apos;s your quote, {name.split(" ")[0]}</h1>
          <p className="text-zinc-600 mt-2">We just texted this to you too. Book any time.</p>
        </div>

        <div className="bg-white rounded-2xl border-2 border-zinc-100 shadow-sm p-5 mb-4">
          <h2 className="text-lg font-bold text-zinc-900 mb-3">Your Screen Door</h2>
          <div className="space-y-1 text-sm text-zinc-700">
            <div>Type: <span className="font-semibold">{service === "new" ? "New Sliding Screen Door" : "Rescreen Existing Door"}</span></div>
            {service === "new" && <div>Color: <span className="font-semibold capitalize">{color}</span></div>}
            <div>Mesh: <span className="font-semibold">{mesh.label}</span></div>
            {service === "new" && heavyDuty && <div>Heavy Duty Upgrade: <span className="font-semibold">Yes</span></div>}
          </div>
        </div>

        {service === "new" && (
          <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 mb-4">
            <div className="text-xs uppercase tracking-wider text-emerald-800 font-bold mb-2">Included FREE</div>
            <div className="space-y-1 text-sm text-emerald-900 font-medium">
              <div className="flex items-center gap-2"><Check className="w-4 h-4" /> Professional Measurement</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4" /> Delivery</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4" /> Professional Installation</div>
            </div>
          </div>
        )}

        {winSummary && (
          <div className="bg-white rounded-2xl border-2 border-zinc-100 shadow-sm p-5 mb-4">
            <h2 className="text-lg font-bold text-zinc-900 mb-2">Plus while we&apos;re there</h2>
            <div className="text-sm text-zinc-700">{winSummary}</div>
          </div>
        )}

        <div className="bg-white rounded-2xl border-2 border-zinc-100 shadow-sm p-5 mb-6">
          <h2 className="text-sm uppercase tracking-wider text-zinc-500 font-bold mb-3">Line Items</h2>
          <div className="space-y-1.5 text-sm">
            {breakdown.items.map((li, i) => (
              <div key={i}>
              <div className="flex justify-between">
                <span className="text-zinc-700">{li.name} × {li.quantity}</span>
                <span className="font-semibold text-zinc-900">{fmt(li.line_total)}</span>
              </div>
              {meshKindFromName(li.name) && <MeshPhoto kind={meshKindFromName(li.name)} compact />}
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-100 mt-3 pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span>{fmt(breakdown.subtotal)}</span></div>
            <div className="flex justify-between text-zinc-600"><span>Tax</span><span>{fmt(breakdown.tax)}</span></div>
            <div className="flex justify-between text-xl font-bold text-[#207024] pt-2">
              <span>Total</span>
              <span data-testid="quote-total">{fmt(breakdown.total)}</span>
            </div>
          </div>
        </div>

        <a
          href={leadId ? `/instant-estimate?resume=${leadId}` : "#"}
          data-testid="book-now-btn"
          className="w-full h-14 rounded-xl bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold flex items-center justify-center gap-2 shadow-lg"
        >
          Check Availability &amp; Book Now
          <ChevronRight className="w-5 h-5" />
        </a>
        <p className="text-center text-xs text-zinc-500 mt-3">Prefer to talk? Just text us back.</p>
      </div>
    </div>
  );
}
